import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { FILE_IMPORT_TYPE, FileImportDetailsType, IMPORT_STATUS } from "api/models";
import { useGetFileImportDetails } from "api/queries/file-import/useGetFileImportDetails";
import Button from "components/atoms/Button/Button";
import { renderFileStatus, stringifyStatusDescription } from "helpers/fileImportHelpers";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { uploadRevisedFile } from "api/services/fileImport";
import { useGetFileImportActions } from "api/queries/file-import/useGetFileImportActions";

export default function ImportDetailsInfo() {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const currentImportId = searchParams.get("import");

  const { permissions } = usePermissions();
  const { permissions: permissionData } = JSON.parse(permissions || "{}");

  const { data: fileImportDetailsData, refetch: refetchImportDetails } = useGetFileImportDetails(currentImportId!, {
    enabled: !!currentImportId,
  });
  const { refetch: refetchValidationHistory } = useGetFileImportActions(currentImportId!, {
    enabled: !!currentImportId,
  });

  const fileImportDetails = fileImportDetailsData as FileImportDetailsType;
  const { data_type } = fileImportDetails?.file_import_job ?? {};
  const { status } = fileImportDetails?.latest_import_job_action ?? {};
  const { attachment: imported_file_attachment } = fileImportDetails?.latest_import_job_action?.imported_file ?? {};

  const allowActions =
    (data_type === FILE_IMPORT_TYPE.USER && permissionData?.admin_settings?.create_view_edit_users) ||
    ((data_type === FILE_IMPORT_TYPE.ADDRESS || data_type === FILE_IMPORT_TYPE.ADDRESS_V2) &&
      !permissionData?.quoting?.restricted_to_address_book) ||
    data_type === FILE_IMPORT_TYPE.CONTACT ||
    data_type === FILE_IMPORT_TYPE.TRACKER ||
    data_type === FILE_IMPORT_TYPE.TRACKER_V2 ||
    (data_type === FILE_IMPORT_TYPE.RATE_CARD && permissionData?.admin_settings?.perform_rate_card_file_import);

  const { mutate: uploadRevisedFileMutation, isLoading: isUploading } = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) => uploadRevisedFile(id, formData),
    onSuccess: () =>
      toast(
        intl.formatMessage({
          id: "new-request.otherAttachment.fileUploaded",
          defaultMessage: "File uploaded",
        }),
        {
          type: "success",
        },
      ),
    onError: (err: any) => errorToast(err),
    onSettled: () => {
      refetchImportDetails();
      refetchValidationHistory();
    },
  });

  const handleUploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx";
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement)?.files?.[0];
      if (file && currentImportId) {
        const formData = new FormData();
        formData.append("uploaded_file", file);
        uploadRevisedFileMutation({ id: currentImportId, formData });
      }
    };
    input.click();
  };

  const data = [
    {
      label: <FormattedMessage id="settings.fileImportTools.fileImportHistory.dataType" defaultMessage="Date Type" />,
      value: <div className="text-capitalize">{fileImportDetails?.file_import_job?.data_type}</div>,
    },
    {
      label: <FormattedMessage id="settings.fileImportTools.fileImportHistory.actionType" defaultMessage="Action Type" />,
      value: <div className="text-capitalize">{fileImportDetails?.file_import_job?.action.replaceAll("_", " ")}</div>,
    },
    {
      label: (
        <FormattedMessage
          id="settings.fileImportTools.fileImportHistory.createdDateAndTime"
          defaultMessage="Created Date & Time"
        />
      ),
      value: moment(fileImportDetails?.file_import_job?.created, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY HH:mm"),
    },
    {
      label: <FormattedMessage id="settings.fileImportTools.fileImportHistory.createdBy" defaultMessage="Created By" />,
      value: fileImportDetails?.file_import_job?.created_by,
    },
    {
      label: <FormattedMessage id="general.description" defaultMessage="Description" />,
      value: fileImportDetails?.file_import_job?.description,
    },
    {
      label: <FormattedMessage id="general.status" defaultMessage="Status" />,
      value: renderFileStatus(status, intl),
    },
    {
      label: (
        <FormattedMessage
          id="settings.fileImportTools.fileImportHistory.statusDescription"
          defaultMessage="Status Description"
        />
      ),
      value: stringifyStatusDescription(status),
    },
    {
      label: <FormattedMessage id="settings.fileImportTools.fileImportHistory.action" defaultMessage="Action" />,
      value: (
        <div>
          {[IMPORT_STATUS.ERRORS, IMPORT_STATUS.FAILED].includes(status) && allowActions && (
            <>
              {imported_file_attachment && (
                <a
                  target="_blank"
                  href={imported_file_attachment}
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <Button className="mb-2" variant="secondary">
                    <FormattedMessage
                      id="settings.fileImportTools.fileImportHistory.downloadFileWithErrorLog"
                      defaultMessage="Download file with error log"
                    />
                  </Button>
                </a>
              )}
              <Button variant="secondary" onClick={handleUploadFile} isLoading={isUploading}>
                {status === IMPORT_STATUS.ERRORS ? (
                  <FormattedMessage
                    id="settings.fileImportTools.fileImportHistory.uploadedRevisedFile"
                    defaultMessage="Upload revised file"
                  />
                ) : (
                  <FormattedMessage id="settings.fileImportTools.fileImportHistory.reupload" defaultMessage="Reupload" />
                )}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {data.map((item, index) => (
        <Row key={index} className="mb-3">
          <Col xs={3}>
            <h6>{item.label}</h6>
          </Col>
          <Col>{item.value}</Col>
        </Row>
      ))}
    </>
  );
}
